import { createContext, Fragment, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  enCatalogue, makeTranslator, dirFor,
  type Catalogue, type Translate, type TranslateOptions,
} from '@transport-booking/shared'
import { setDateLocale, setTzLabel } from './format'

/**
 * Language for the whole app.
 *
 * English ships in the bundle and renders immediately. The chosen language is
 * fetched as an OVERLAY and applied when it arrives, so there is never a moment
 * where the interface has no words — no spinner, no flash of raw keys, and no
 * broken screen if the request fails entirely.
 *
 * The choice is kept in localStorage rather than only on the profile, because
 * the sign-in page needs translating too and nobody is signed in there yet.
 * When a signed-in user has a language on their profile, that wins on first
 * load — it is the answer they gave during onboarding.
 */

const KEY = 'tb.mobile.lang'
const API = (import.meta.env.VITE_API_URL as string | undefined) ?? '/v1'

interface Ctx {
  locale: string
  dir: 'ltr' | 'rtl'
  t: Translate
  setLocale: (l: string) => void
  ready: boolean
}

const I18nContext = createContext<Ctx | null>(null)

function stored(): string | null {
  try { return localStorage.getItem(KEY) } catch { return null }
}

export function I18nProvider({ children, profileLocale }: {
  children: ReactNode
  /** The language on the signed-in profile, if there is one. */
  profileLocale?: string | null
}) {
  const [locale, setLocaleState] = useState<string>(() => stored() ?? 'en')
  const [overrides, setOverrides] = useState<Catalogue>({})
  const [ready, setReady] = useState(false)

  // A profile language is the answer this person actually gave, so it wins on
  // first load — but only until they change it here, which is why it is written
  // through to storage rather than applied on every render.
  useEffect(() => {
    if (!profileLocale) return
    if (stored()) return
    setLocaleState(profileLocale)
    try { localStorage.setItem(KEY, profileLocale) } catch { /* ignore */ }
  }, [profileLocale])

  useEffect(() => {
    let cancelled = false
    if (locale === 'en') { setOverrides({}); setReady(true); return }

    setReady(false)
    fetch(`${API}/i18n/${encodeURIComponent(locale)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { strings: Catalogue }) => { if (!cancelled) setOverrides(d.strings ?? {}) })
      // A failed fetch is not an error state. English is already on screen and
      // stays there; a language nobody can download is not worth a broken app.
      .catch(() => { if (!cancelled) setOverrides({}) })
      .finally(() => { if (!cancelled) setReady(true) })

    return () => { cancelled = true }
  }, [locale])

  const dir = dirFor(locale)

  // Dates, times and relative phrases are formatted outside React, so the
  // locale has to be pushed to them rather than read from context. Set
  // during render, not in an effect: a child further down this same render
  // may format a date before any effect has run.
  setDateLocale(locale)

  // Direction belongs on <html>, not on a wrapper: it has to reach portals,
  // dialogs and the scrollbar, none of which are inside the React tree.
  useEffect(() => {
    document.documentElement.setAttribute('lang', locale)
    document.documentElement.setAttribute('dir', dir)
  }, [locale, dir])

  const t = useMemo(
    () => makeTranslator({ locale, en: enCatalogue, overrides }),
    [locale, overrides],
  )

  // Same reason as setDateLocale: the formatters live outside React.
  setTzLabel(t('time.ksa'))

  const value = useMemo<Ctx>(() => ({
    locale, dir, t, ready,
    setLocale: (l: string) => {
      try { localStorage.setItem(KEY, l) } catch { /* ignore */ }
      setLocaleState(l)
    },
  }), [locale, dir, t, ready])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): Ctx {
  const c = useContext(I18nContext)
  if (!c) throw new Error('useI18n must be used inside I18nProvider')
  return c
}

/** The common case: `const t = useT()`. */
export function useT(): Translate {
  return useI18n().t
}

/**
 * One translated sentence with a React node inside it -- a bolded email, a
 * city, a date.
 *
 *   <Msg k="signIn.sentCode" nodes={{ email: <strong>{email}</strong> }} />
 *   en: 'We sent a 6-digit code to {{email}}. It expires in 10 minutes.'
 *
 * The alternative is cutting the sentence at the markup and translating the
 * halves separately, which cannot work: Arabic and Urdu put the value in a
 * different place, and a translator handed "We sent a 6-digit code to" has no
 * way to say so. The whole sentence stays one row in the CSV.
 *
 * It works by translating with unprintable sentinels in place of the nodes and
 * splitting on them afterwards, so lookup, fallback and plurals all still go
 * through the same translator as every other string.
 */
export function Msg({ k, vars, nodes }: {
  k: string
  vars?: TranslateOptions
  nodes: Record<string, ReactNode>
}) {
  const t = useT()
  const names = Object.keys(nodes)
  const marks: Record<string, string> = {}
  names.forEach((n, i) => { marks[n] = `\u0000${i}\u0000` })

  const parts = t(k, { ...vars, ...marks }).split(/\u0000(\d+)\u0000/)
  return (
    <>
      {parts.map((part, i) => {
        if (i % 2 === 0) return part
        const name = names[Number(part)]
        return <Fragment key={i}>{name ? nodes[name] : null}</Fragment>
      })}
    </>
  )
}
